import posthog from "posthog-js";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import { isEmbedded, postToParent } from "~/lib/embed";
import { useBookingFlowStore } from "~/stores/booking-flow-store";
import GTMContext, { GTMEvent } from "./GTMContext";

interface GTMProviderProps {
  children: React.ReactNode;
}

const GTMProvider = ({ children }: GTMProviderProps) => {

  const { scuid, setScuid, ip, setIp, hasBookedBefore, setHasBookedBefore } = useBookingFlowStore(useShallow((state) => ({
    scuid: state.scuid,
    setScuid: state.setScuid,
    ip: state.ip,
    setIp: state.setIp,
    hasBookedBefore: state.hasBookedBefore,
    setHasBookedBefore: state.setHasBookedBefore,
  })));

  const trpcUtils = trpc.useUtils();

  const preventDuplicateEvent = (name: GTMEvent) => {
    const doubleEvents = [
      GTMEvent.BOOKING_FUNNEL_INIT,
      GTMEvent.BOOKING_FUNNEL_SERVICE_SELECTION,
      GTMEvent.BOOKING_FUNNEL_APPOINTMENT_SELECTION,
      GTMEvent.BOOKING_FUNNEL_SIGNUP_WAITLIST,
      GTMEvent.BOOKING_FUNNEL_APPOINTMENT_SELECTED,
      GTMEvent.BOOKING_FUNNEL_CONTACT_DETAILS,
      GTMEvent.BOOKING_FUNNEL_PATH_SELECTION,
      GTMEvent.BOOKING_FUNNEL_CONFIRMATION,
      GTMEvent.WAITLIST_SLOT_VIEWED,
    ];
    const lastEvent = window.dataLayer?.[window.dataLayer.length - 1];
    return !!lastEvent && doubleEvents.includes(lastEvent.event) && lastEvent.event === name;
  };

  const providerValue = useMemo(() => {
    const _sendEvent = async (name: GTMEvent, data: any) => {
      let scuidValue: string | null = scuid;
      let ipValue: string | null = ip;
      let hasBookedBeforeValue: boolean | null = hasBookedBefore;

      // Enrichment is best-effort: never let a failure here prevent the event
      // itself from being pushed to GTM / captured in PostHog.
      try {
        if(!scuid) {
          try {
            const { scuid: fetchedScuid } = await trpcUtils.publicUtilities.getScuid.fetch();
            setScuid(fetchedScuid ?? null);
            scuidValue = fetchedScuid ?? null;
            // console.log("Set scuid to:", fetchedScuid);
          } catch(e) {
            console.log("Failed to fetch scuid:", e);
          }
        }

        if(!ip) {
          try {
            const { ip: fetchedIp } = await trpcUtils.publicUtilities.getMyIp.fetch();
            setIp(fetchedIp ?? null);
            ipValue = fetchedIp ?? null;
            // console.log("Set IP to:", fetchedIp);
          } catch(e) {
            console.log("Failed to fetch ip:", e);
          }
        }

        const userEmail = data?.user?.email;
        if(hasBookedBefore === null && typeof userEmail === "string" && userEmail.includes("@")) {
          try {
            const { hasBookedBefore: fetchedHasBookedBefore } = await trpcUtils.publicUtilities.haveIBookedBefore.fetch({ email: userEmail });
            setHasBookedBefore(fetchedHasBookedBefore);
            hasBookedBeforeValue = fetchedHasBookedBefore;
            // console.log("Set has booked before to:", fetchedHasBookedBefore);
          } catch(e) {
            console.log("Failed to fetch has booked before:", e);
          }
        }
      } catch(e) {
        console.log("Failed to enrich analytics event: ", e);
      }

      try {
        if (preventDuplicateEvent(name)) {
          return;
        }

        // Tag every event so deeplink sessions can be distinguished from normal
        // funnel sessions. Read from the store at send time so the value is always current.
        const { deeplinkSlug, deeplinkPractitionerId, deeplinkTargetCategoryId, preferredPractitionerId } = useBookingFlowStore.getState();
        const entryData = {
          entry_point: deeplinkSlug ? 'deeplink' : 'booking_funnel',
          deeplink_slug: deeplinkSlug ?? undefined,
          deeplink_practitioner_id: deeplinkPractitionerId ?? undefined,
          deeplink_category_id: deeplinkTargetCategoryId ?? undefined,
          preferred_practitioner_id: preferredPractitionerId ?? undefined,
          preferred_practitioner_selected: preferredPractitionerId != null,
          embedded: isEmbedded(),
        };

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: name,
          scuid: scuidValue,
          ip: ipValue,
          isFirstBooking: hasBookedBeforeValue === null ? undefined : !hasBookedBeforeValue,
          ...entryData,
          ...data,
        });

        posthog.capture(name, { ...entryData, ...Object.fromEntries(Object.entries(data).flatMap(([key, value]) => 
            typeof value === 'object' && value !== null && !Array.isArray(value)
                ? Object.entries(value).map(([nestedKey, nestedValue]) => [`${nestedKey}`, nestedValue])
                : [[key, value]]
        ))});

        // The parent page feeds its own dataLayer and third-party pixels, so contact details are not forwarded.
        const { user: _user, ...dataWithoutUser } = data ?? {};
        postToParent("analytics", JSON.parse(JSON.stringify({ event: name, ...entryData, ...dataWithoutUser })));
      } catch (e) {
        console.log("Failed to send analytics event: ", e);
      }
    };

    const sendEvent = (name: GTMEvent, data: any) => {
      _sendEvent(name, data);
    }

    return {
      sendEvent,
    };
  }, [scuid, ip]);
  return (
    <GTMContext.Provider value={providerValue}>{children}</GTMContext.Provider>
  );
};

export default GTMProvider;
